import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_15, FONT_WEIGHT_PROXIMA_SEMIBOLD, FONT_PROXIMA_13} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';

export interface CardGridProps {
  children?: React.ReactNode;
  minWidth?: string;
}

/**
 *
 * CardGrid is useful for rendering a grid containing elements of the same size (width and height), Card.
 * You usually do not need to control the size of individual Card, as CardGrid will stretch them to fit.
 * You can optionally specify a minWidth [default: '200px'] to control the minimum width of each Card.
 * If multiple Cards can fit on the same row without being narrower than the minWidth, then they are placed on the same row.
 * Else, Cards are placed on separate rows until no Cards are narrower than the minWidth.
 * CardGrid do not control the height of the Cards, you may control the height of the Cards yourself.
 *
 * @example:
 * <CardGrid minWidth='125px'>
 *   <Card title='Left join' description='SQL style left join' />
 *   <Card icon={<BenchlingIcon icon={'right_join.svg'}/>}
 *     title={<b>Right join</b>}
 *     description={<small>SQL style right join</small>}
 *   />
 * </CardGrid>
 *
 *
 * @example:
 * // CardGrid with clickable, customized Card
 * <CardGrid minWidth='125px'>
 *   {['left_join', 'right_join', 'outer_join'].map(joinType => (
 *     <Card key={joinType}
 *       icon={<BenchlingIcon icon={`${joinType}.svg`}/>}
 *       title={joinType}
 *       description={`SQL style ${joinType}`}
 *       containerProps={
 *         onClick: ()=>setJoinType(joinType),
 *         styles: state.joinType === joinType
 *           ? [styles.cardButton, styles.cardButton___Active)
 *           : styles.cardButton),
 *       }}
 *     />
 *   )}
 * </CardGrid>
 *
 * @example:
 * // CardGrid is happy to render any children if you are not happy with the Card layout component
 * <CardGrid minWidth='125px'>
 *   <div>Among Us</div>
 * </CardGrid>
 */
export default function CardGrid({children, minWidth = '200px'}: CardGridProps): JSX.Element {
  return (
    <div
      className={css(styles.cardGridContainer)}
      style={{gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}, 1fr))`, gridAutoRows: '1fr'}}
    >
      {children}
    </div>
  );
}

export interface CardProps {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  containerProps?: Omit<React.HTMLProps<HTMLDivElement>, 'className'> & {styles?: object};
}

/**
 * Card is just a default layout for CardGrid for rendering a card with an icon, title, and description.
 *
 * @example:
 * <Card title='Left join' description='SQL style left join' />
 *
 * @example:
 * // Card's icon, title, and description can be customized with JSX element
 * <Card icon={<BenchlingIcon icon={'right_join.svg'}/>}
 *  title={<b>Right join</b>}
 *  description={<small>SQL style right join</small>}
 * />
 *
 * @example:
 * // The outer most Card container may be customized with containerProps.
 * // Note, to style the container, use `styles` and not `className`.
 * // Ref: https://github.com/Khan/aphrodite?tab=readme-ov-file#overriding-styles
 * <Card key={joinType}
 *   icon={<BenchlingIcon icon={`${joinType}.svg`}/>}
 *   title={joinType}
 *   description={`SQL style ${joinType}`}
 *   containerProps={{
 *     onClick: ()=>setJoinType(joinType),
 *     styles: state.joinType === joinType
 *       ? [styles.cardButton, styles.cardButton___Active]
 *       : styles.cardButton,
 *   }}
 * />
 */
export function Card({
  icon = null,
  title = null,
  description = null,
  containerProps = {},
}: CardProps): JSX.Element {
  const {styles: containerStyles, ...containerPropsCopy} = containerProps;
  const containerClassName = css(styles.cardContainer, containerStyles);
  return (
    <div {...containerPropsCopy} className={containerClassName}>
      <div className={css(styles.cardIcon)}>{icon}</div>
      <div>
        <div className={css(styles.cardTitle)}>{title}</div>
        <div className={css(styles.cardDescription)}>{description}</div>
      </div>
    </div>
  );
}

export const styles = StyleSheet.create({
  cardGridContainer: {
    display: 'grid',
    gap: '15px',
  },
  cardContainer: {
    border: `1px solid ${Colors.GRAY}`,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    gap: '15px',
    padding: '10px',
  },
  cardIcon: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  cardTitle: {
    ...FONT_PROXIMA_15,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Colors.NEAR_BLACK,
    textAlign: 'justify',
  },
  cardDescription: {
    ...FONT_PROXIMA_13,
  },
});
